/**
 * Upload Progress Slice
 *
 * App-level Redux state for the global video upload progress overlay.
 * Mirrors Flutter's `VideoUploadProgressNotifier`.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/presentation/store';

export interface UploadGlobalOverlayProgressState {
  isUploading: boolean;
  progress: number;
  message: string | null;
}

const initialState: UploadGlobalOverlayProgressState = {
  isUploading: false,
  progress: 0,
  message: null,
};

const uploadGlobalOverlayProgressSlice = createSlice({
  name: 'uploadGlobalOverlayProgress',
  initialState,
  reducers: {
    startUpload: (state, action: PayloadAction<{ message?: string | null } | undefined>) => {
      state.isUploading = true;
      state.progress = 0;
      state.message = action.payload?.message ?? null;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = Math.max(0, Math.min(100, action.payload));
    },
    endUpload: (state) => {
      state.isUploading = false;
      state.progress = 0;
      state.message = null;
    },
  },
});

export const { startUpload, setProgress, endUpload } = uploadGlobalOverlayProgressSlice.actions;
export default uploadGlobalOverlayProgressSlice.reducer;

export const selectUploadProgress = (state: RootState): UploadGlobalOverlayProgressState =>
  state.uploadProgress;

export const selectIsUploading = (state: RootState): boolean =>
  state.uploadProgress.isUploading;
